import baseAPI from '@/api/base';
import {
  EditExportTemplate,
  GenerateExportTemplate,
  GetExportTemplate,
  GetExportTemplates,
  RegisterExportTemplate,
  RunExportTemplate,
} from '@/dtos/export_template.dto';
import {
  DELETE_EXPORT_TEMPLATE_URL,
  DUPLICATE_EXPORT_TEMPLATE_URL,
  EDIT_EXPORT_TEMPLATE_URL,
  GENERATE_EXPORT_TEMPLATE_URL,
  GET_EXPORT_DATASET_FIELDS_URL,
  GET_EXPORT_DATASETS_URL,
  GET_EXPORT_TEMPLATE_URL,
  REGISTER_EXPORT_TEMPLATE_URL,
  RUN_EXPORT_TEMPLATE_URL,
} from '@/utils/urls';
import { AxiosResponse } from 'axios';

/** Leaves out empty query values so the API does not filter on them. */
const compactParams = (params: Record<string, any> = {}): Record<string, any> =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''));

class ExportTemplate {
  static getExportTemplates = async (params: GetExportTemplates | GetExportTemplate = {}): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_EXPORT_TEMPLATE_URL, {
      params: compactParams(params),
    });
    return response;
  };

  static registerExportTemplate = async (payload: RegisterExportTemplate): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(REGISTER_EXPORT_TEMPLATE_URL, payload);
    return response;
  };

  static editExportTemplate = async (payload: EditExportTemplate): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(EDIT_EXPORT_TEMPLATE_URL, payload);
    return response;
  };

  static deleteExportTemplate = async (exportTemplateId: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.delete(`${DELETE_EXPORT_TEMPLATE_URL}/${encodeURIComponent(exportTemplateId)}`);
    return response;
  };

  static duplicateExportTemplate = async (exportTemplateId: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(`${DUPLICATE_EXPORT_TEMPLATE_URL}/${encodeURIComponent(exportTemplateId)}/duplicate`);
    return response;
  };

  static getExportDatasets = async (): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_EXPORT_DATASETS_URL);
    return response;
  };

  static getExportDatasetFields = async (dataset: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(`${GET_EXPORT_DATASET_FIELDS_URL}/${encodeURIComponent(dataset)}/fields`);
    return response;
  };

  /**
   * Silent: the caller reports failures (the promise rejects with the axios error). A preview answers
   * with JSON, a file with a Blob whose error body the interceptor turns back into JSON.
   */
  static runExportTemplate = async (payload: RunExportTemplate): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(RUN_EXPORT_TEMPLATE_URL, compactParams(payload), {
      silent: true,
      ...(payload.output === 'file' ? { responseType: 'blob' as const } : {}),
    });
    return response;
  };

  static generateExportTemplate = async (generateExportTemplate: GenerateExportTemplate): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(GENERATE_EXPORT_TEMPLATE_URL, generateExportTemplate, {
      responseType: 'blob',
    });
    return response;
  };
}

export default ExportTemplate;
